/**
 * Externe Recherche - Tab-Seite (kein Dialog)
 * 
 * 1. User beschreibt Sachverhalt + lädt Dokumente hoch
 * 2. System extrahiert Text aus Dokumenten + sammelt Aufgaben-Kontext
 * 3. Alles wird pseudonymisiert zur Prüfung angezeigt
 * 4. Nach Bestätigung: GPT antwortet: de-pseudonymisiert
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabSearchParams } from '@/hooks/useTabRoute';
import {
  Shield, Send, Eye, Loader2, X, Search,
  AlertTriangle, CheckCircle, Volume2, VolumeX, Copy, Check,
  Upload, FileText, Paperclip, Briefcase, Plus, ArrowRight, ArrowLeft,
  Mic, MicOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle
} from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { toast } from 'sonner';

export default function ExterneRecherchePage() {
  const navigate = useNavigate();
  const [searchParams] = useTabSearchParams();
  
  const kontextTitel = searchParams.get('kontextTitel') || '';
  const kontextId = searchParams.get('kontextId') || '';
  const kontextTyp = searchParams.get('kontextTyp') || '';
  const aufgabeId = searchParams.get('aufgabeId') || '';

  const [schritt, setSchritt] = useState('eingabe');
  const [anfrage, setAnfrage] = useState('');
  const [dateien, setDateien] = useState([]);
  const [pseudonymisiert, setPseudonymisiert] = useState('');
  const [originalGesamt, setOriginalGesamt] = useState('');
  const [antwort, setAntwort] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [docCount, setDocCount] = useState(0);
  const [hasKontext, setHasKontext] = useState(false);
  const [passendeAufgaben, setPassendeAufgaben] = useState([]);
  const [creatingTask, setCreatingTask] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Aufgabe-Erstellen State
  const [showAufgabeForm, setShowAufgabeForm] = useState(false);
  const [titelVorschlaege, setTitelVorschlaege] = useState([]);
  const [aufgabeForm, setAufgabeForm] = useState({ titel: '', klient_id: '', massnahme_id: '', prioritaet: 'mittel' });
  const [klienten, setKlienten] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);

  // Spracheingabe
  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Spracherkennung nicht unterstützt'); return; }
    const recognition = new SR();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join('');
      setAnfrage(transcript);
    };
    recognition.onend = () => { setIsListening(false); recognitionRef.current = null; };
    recognition.onerror = () => { setIsListening(false); recognitionRef.current = null; };
    recognitionRef.current = recognition;
    recognition.start();
  };
  const stopListening = () => { if (recognitionRef.current) recognitionRef.current.stop(); };

  const removeFile = (idx) => setDateien(prev => prev.filter((_, i) => i !== idx));

  // Schritt 1 -> 2: Dokumente extrahieren + pseudonymisieren
  const handlePseudonymisieren = async () => {
    if (!anfrage.trim() && dateien.length === 0) {
      toast.error('Bitte beschreiben Sie den Sachverhalt oder laden Sie Dokumente hoch');
      return;
    }
    setLoading(true);
    try {
      const result = await betreuungAPI.rechercheMitDokumenten(
        anfrage,
        { aufgabeId, kontextTyp, kontextId, dateien }
      );
      setPseudonymisiert(result.pseudonymisiert || '');
      setOriginalGesamt(result.original || anfrage);
      setDocCount(result.dokumente_count || 0);
      setHasKontext(result.kontext_vorhanden || false);
      setSchritt('pruefung');
    } catch {
      toast.error('Fehler bei der Verarbeitung');
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2 -> 3: An GPT senden
  const handleAbsenden = async () => {
    setLoading(true);
    try {
      const result = await betreuungAPI.rechercheAbsenden(originalGesamt, pseudonymisiert, kontextId);
      if (result.success) {
        setAntwort(result.antwort);
        setPassendeAufgaben(result.passende_aufgaben || []);
        setSchritt('antwort');
      } else {
        toast.error(result.antwort || 'Fehler');
      }
    } catch {
      toast.error('Fehler bei der Anfrage');
    } finally {
      setLoading(false);
    }
  };

  // Neue Aufgabe aus Recherche-Ergebnis anlegen
  const handleNeueAufgabeStart = async () => {
    const vorschlaege = [];
    const kurzAnfrage = anfrage.replace(/[?!.]/g, '').trim();
    if (kurzAnfrage.length > 10) vorschlaege.push(kurzAnfrage.slice(0, 60));
    const handlung = antwort.match(/HANDLUNGSBEDARF:?\s*(.{10,80})/i);
    if (handlung) vorschlaege.push(handlung[1].replace(/\*\*/g, '').trim());
    const schritte = antwort.match(/(?:NÄCHSTE SCHRITTE|1\.)\s*(.{10,60})/i);
    if (schritte) vorschlaege.push(schritte[1].replace(/\*\*/g, '').trim());
    if (vorschlaege.length === 0) vorschlaege.push('Neue Aufgabe aus Recherche');

    setTitelVorschlaege([...new Set(vorschlaege)].slice(0, 3));
    setAufgabeForm({ titel: vorschlaege[0] || '', klient_id: kontextId || '', massnahme_id: '', prioritaet: 'mittel' });

    try {
      const [kl, ma] = await Promise.all([
        betreuungAPI.listKlienten(),
        betreuungAPI.listMassnahmen(),
      ]);
      setKlienten(kl);
      setMassnahmen(ma);
    } catch {}

    setShowAufgabeForm(true);
  };

  const handleNeueAufgabeErstellen = async () => {
    if (!aufgabeForm.titel.trim()) { toast.error('Bitte einen Titel eingeben'); return; }
    setCreatingTask(true);
    try {
      const data = {
        titel: aufgabeForm.titel,
        beschreibung: `Aus externer Recherche:\n\n${anfrage}`,
        prioritaet: aufgabeForm.prioritaet,
        status: 'offen',
      };
      if (aufgabeForm.klient_id) {
        data.klient_id = aufgabeForm.klient_id;
        const kl = klienten.find(k => k.id === aufgabeForm.klient_id);
        if (kl) data.betreuter_name = `${kl.vorname} ${kl.nachname}`;
      }
      if (aufgabeForm.massnahme_id) {
        data.massnahme_id = aufgabeForm.massnahme_id;
        const ma = massnahmen.find(m => m.id === aufgabeForm.massnahme_id);
        if (ma) data.massnahme_bezeichnung = ma.bezeichnung || ma.typ;
      }

      const result = await betreuungAPI.createAufgabe(data);
      await betreuungAPI.createAufgabenVerlauf(result.id, {
        text: `Externe Recherche durchgeführt:\n${antwort.slice(0, 1500)}`,
        typ: 'aktion',
      });
      toast.success('Aufgabe angelegt');
      setShowAufgabeForm(false);
      navigate(`/aufgaben/${result.id}`);
    } catch {
      toast.error('Fehler beim Anlegen');
    } finally {
      setCreatingTask(false);
    }
  };

  // In bestehende Aufgabe übernehmen
  const handleInAufgabeUebernehmen = async (aufgabeIdTarget) => {
    try {
      await betreuungAPI.createAufgabenVerlauf(aufgabeIdTarget, {
        text: `Externe Recherche:\n${anfrage}\n\n--- KI-Empfehlung ---\n${antwort.slice(0, 1000)}`,
        typ: 'aktion',
      });
      toast.success('Recherche-Ergebnis in Aufgabe übernommen');
      navigate(`/aufgaben/${aufgabeIdTarget}`);
    } catch {
      toast.error('Fehler');
    }
  };

  // Erkannte Entitäten
  const [erkannteEntities, setErkannteEntities] = useState([]);
  useEffect(() => {
    if (antwort) {
      const entities = [];
      const instPatterns = [
        /(?:Jobcenter|Sozialamt|Amtsgericht|Landratsamt|Finanzamt|Rentenversicherung|AOK|TK|Barmer|DAK|IKK|Sparkasse|Volksbank|Commerzbank|Postbank)\s+[A-Za-zäöüÄÖÜ\-]+/gi,
        /(?:Krankenversicherung|Bank|Gericht|Behörde)[:\s]+([A-Za-zäöüÄÖÜ\-\s]{3,30})/gi
      ];
      instPatterns.forEach(p => {
        const matches = antwort.match(p) || [];
        matches.forEach(m => {
          if (!entities.find(e => e.name === m.trim())) {
            entities.push({ typ: 'institution', name: m.trim() });
          }
        });
      });
      const firmen = antwort.match(/[A-ZÄÖÜ][a-zäöü]+(?:\s+[A-ZÄÖÜ][a-zäöü]+)*\s+(?:GmbH|UG|AG|KG|GbR|e\.V\.|eG)/g) || [];
      firmen.forEach(f => {
        if (!entities.find(e => e.name === f)) {
          entities.push({ typ: 'institution', name: f });
        }
      });
      setErkannteEntities(entities.slice(0, 5));
    }
  }, [antwort]);

  const handleEntityAnlegen = async (entity) => {
    try {
      if (entity.typ === 'institution') {
        const typ = entity.name.toLowerCase().includes('gericht') ? 'gericht' :
                    entity.name.toLowerCase().includes('jobcenter') || entity.name.toLowerCase().includes('amt') ? 'behoerde' :
                    entity.name.toLowerCase().includes('sparkasse') || entity.name.toLowerCase().includes('bank') ? 'bank' :
                    entity.name.toLowerCase().includes('versicherung') || entity.name.toLowerCase().includes('aok') || entity.name.toLowerCase().includes('tk') ? 'krankenversicherung' :
                    'sonstiges';
        await betreuungAPI.createJuristischePerson({ name: entity.name, typ });
        toast.success(`Institution "${entity.name}" angelegt`);
      }
      setErkannteEntities(prev => prev.filter(e => e.name !== entity.name));
    } catch { toast.error('Fehler beim Anlegen'); }
  };

  const handleVorlesen = () => {
    if (isSpeaking) { window.speechSynthesis.cancel(); setIsSpeaking(false); return; }
    const clean = antwort.replace(/\*\*/g, '').replace(/\n/g, '. ').replace(/- /g, '');
    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = 'de-DE';
    utterance.rate = 0.95;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const handleKopieren = () => {
    navigator.clipboard.writeText(antwort.replace(/\*\*/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="externe-recherche-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              Externe Recherche
            </h1>
            {kontextTitel && <Badge variant="secondary" className="text-xs mt-1">{kontextTitel}</Badge>}
          </div>
        </div>
      </div>

      {/* Schritt-Anzeige */}
      <div className="flex items-center gap-2">
        {[
          { nr: 1, label: 'Sachverhalt', key: 'eingabe' },
          { nr: 2, label: 'Prüfen', key: 'pruefung' },
          { nr: 3, label: 'Antwort', key: 'antwort' },
        ].map((s, i) => {
          const steps = ['eingabe', 'pruefung', 'antwort'];
          const currentIdx = steps.indexOf(schritt);
          const stepIdx = steps.indexOf(s.key);
          const isDone = stepIdx < currentIdx;
          const isCurrent = stepIdx === currentIdx;
          return (
            <div key={s.key} className="flex items-center gap-2">
              {i > 0 && <div className={`w-8 h-0.5 ${isDone ? 'bg-green-400' : 'bg-slate-200'}`} />}
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                isCurrent ? 'bg-blue-600 text-white' : isDone ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {isDone ? <Check className="w-3 h-3" /> : s.nr}
              </div>
              <span className={`text-xs ${isCurrent ? 'text-blue-600 font-medium' : 'text-slate-400'}`}>{s.label}</span>
            </div>
          );
        })}
      </div>

      {/* SCHRITT 1: Eingabe + Dokumente */}
      {schritt === 'eingabe' && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div>
              <Label>Ihr Sachverhalt / Ihre Frage</Label>
              <div className="relative mt-1">
                <Textarea
                  value={anfrage}
                  onChange={e => setAnfrage(e.target.value)}
                  rows={5}
                  placeholder={isListening ? 'Ich höre zu...' : 'z.B.: Mein Betreuter hat Schulden beim Jobcenter...'}
                  className="pr-12"
                  data-testid="recherche-eingabe"
                />
                <button
                  onClick={isListening ? stopListening : startListening}
                  className={`absolute right-2 top-2 w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                    isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                  title={isListening ? 'Aufnahme stoppen' : 'Spracheingabe'}
                  data-testid="recherche-mic-btn"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {aufgabeId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 flex items-start gap-2">
                <Briefcase className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Der gesamte Sachverhalt dieser Aufgabe wird automatisch mit einbezogen und pseudonymisiert.</span>
              </div>
            )}

            {/* Dokumente hochladen */}
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Dokumente beifügen (optional)
              </Label>
              <p className="text-xs text-slate-500 mt-0.5 mb-2">
                PDFs, Bescheide, Briefe — der Text wird extrahiert und pseudonymisiert mit einbezogen
              </p>

              {dateien.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {dateien.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                      <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input ref={fileInputRef} type="file" multiple className="hidden"
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.tiff,.eml"
                onChange={e => { setDateien(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                data-testid="recherche-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> Dateien auswählen
              </Button>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Sie können echte Namen und Daten verwenden. Alles wird im nächsten Schritt automatisch pseudonymisiert.</span>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => navigate(-1)}>Abbrechen</Button>
              <Button onClick={handlePseudonymisieren} disabled={loading || (!anfrage.trim() && dateien.length === 0)} data-testid="recherche-pseudonymisieren">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                Pseudonymisieren & Prüfen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SCHRITT 2: Prüfung */}
      {schritt === 'pruefung' && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-slate-500">Prüfen Sie die pseudonymisierte Version. Nur diese Daten gehen nach außen.</p>
            <div className="flex flex-wrap gap-2">
              {docCount > 0 && <Badge className="bg-blue-600 text-white text-xs">{docCount} Dokument(e) extrahiert & einbezogen</Badge>}
              {hasKontext && <Badge className="bg-teal-600 text-white text-xs">Aufgaben-Kontext einbezogen</Badge>}
            </div>

            <div>
              <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                <Eye className="w-3 h-3" /> Vollständiger Text der an die KI gesendet wird:
              </Label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 whitespace-pre-wrap max-h-[350px] overflow-y-auto font-mono text-xs leading-relaxed">
                {pseudonymisiert || 'Kein Text vorhanden'}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Nur die oben gezeigte pseudonymisierte Version wird an den externen KI-Dienst gesendet.</span>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setSchritt('eingabe')}>Zurück bearbeiten</Button>
              <Button onClick={handleAbsenden} disabled={loading} data-testid="recherche-absenden">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Bestätigen & Absenden
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SCHRITT 3: Antwort */}
      {schritt === 'antwort' && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm text-slate-500">Antwort (mit Ihren echten Daten wiederhergestellt):</p>
            <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap max-h-[300px] overflow-y-auto leading-relaxed"
              data-testid="recherche-antwort">
              {antwort}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleVorlesen} data-testid="recherche-vorlesen">
                {isSpeaking ? <VolumeX className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
                {isSpeaking ? 'Stoppen' : 'Vorlesen'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleKopieren}>
                {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Kopiert!' : 'Kopieren'}
              </Button>
            </div>

            {/* Aufgaben-Integration */}
            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">Ergebnis weiterverarbeiten:</p>

              {erkannteEntities.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-purple-700 mb-2">Erkannte Institutionen/Firmen:</p>
                  <div className="flex flex-wrap gap-2">
                    {erkannteEntities.map((e, i) => (
                      <button key={i} onClick={() => handleEntityAnlegen(e)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-purple-200 rounded-full text-xs text-purple-800 hover:bg-purple-100 transition-colors">
                        <Plus className="w-3 h-3" />
                        {e.name}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-purple-600 mt-2">Klicken um als Institution anzulegen</p>
                </div>
              )}

              {passendeAufgaben.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Passende offene Aufgaben gefunden:</p>
                  <div className="space-y-1.5">
                    {passendeAufgaben.map(a => (
                      <button key={a.id} onClick={() => handleInAufgabeUebernehmen(a.id)}
                        className="w-full flex items-center justify-between p-2.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm transition-colors text-left">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-blue-800 truncate block">{a.titel}</span>
                          <span className="text-xs text-blue-600">{a.status} - {a.prioritaet}</span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-blue-500 shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={handleNeueAufgabeStart} disabled={creatingTask}
                data-testid="recherche-neue-aufgabe">
                <Plus className="w-4 h-4 mr-2" />
                Neue Aufgabe mit Recherche-Ergebnis anlegen
              </Button>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Die Antwort wurde automatisch de-pseudonymisiert. Ihre echten Daten wurden nur lokal verarbeitet.</span>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => navigate(-1)}>Schließen</Button>
              <Button variant="outline" onClick={() => { setSchritt('eingabe'); setAnfrage(''); setDateien([]); }}>
                <Search className="w-4 h-4 mr-2" /> Neue Recherche
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aufgabe-Erstellen-Formular (Inline) */}
      {showAufgabeForm && (
        <Card className="border-blue-200 shadow-lg" data-testid="aufgabe-aus-recherche-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="w-5 h-5" /> Aufgabe aus Recherche anlegen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowAufgabeForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Titelvorschläge */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Titel-Vorschläge (klicken zum Übernehmen):</Label>
              <div className="space-y-1.5">
                {titelVorschlaege.map((v, i) => (
                  <button key={i} onClick={() => setAufgabeForm(p => ({ ...p, titel: v }))}
                    className={`w-full text-left p-2.5 rounded-lg text-sm transition-all border ${
                      aufgabeForm.titel === v ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-slate-50 border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Titel (frei bearbeitbar)</Label>
              <Input value={aufgabeForm.titel} onChange={e => setAufgabeForm(p => ({ ...p, titel: e.target.value }))}
                placeholder="Aufgabentitel..." data-testid="aufgabe-dialog-titel" />
            </div>

            <div>
              <Label>Betreuter</Label>
              <Select value={aufgabeForm.klient_id || 'none'} onValueChange={v => setAufgabeForm(p => ({ ...p, klient_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Keiner --</SelectItem>
                  {klienten.map(k => <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Massnahme / Bezug</Label>
              <Select value={aufgabeForm.massnahme_id || 'none'} onValueChange={v => setAufgabeForm(p => ({ ...p, massnahme_id: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="Auswählen..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Keine --</SelectItem>
                  {massnahmen.map(m => <SelectItem key={m.id} value={m.id}>{m.bezeichnung || m.typ}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Priorität</Label>
              <div className="flex gap-2 mt-1">
                {[
                  { value: 'dringend', label: 'Dringend', color: 'bg-red-500' },
                  { value: 'mittel', label: 'Mittelfristig', color: 'bg-orange-400' },
                  { value: 'niedrig', label: 'Gering', color: 'bg-green-500' },
                ].map(p => (
                  <button key={p.value} onClick={() => setAufgabeForm(f => ({ ...f, prioritaet: p.value }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all border ${
                      aufgabeForm.prioritaet === p.value
                        ? `${p.color} text-white border-transparent`
                        : 'bg-white text-slate-600 border-slate-200'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAufgabeForm(false)}>Abbrechen</Button>
              <Button onClick={handleNeueAufgabeErstellen} disabled={creatingTask}>
                {creatingTask ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Aufgabe anlegen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
